/**
 * Phase 6c.1 follow-up — `<MarkTodayCompleteButton>`.
 *
 * Primary completion action for today's session. Lives DIRECTLY BELOW
 * the TodayHero so the daughter can mark today done without scrolling
 * to the This Week list or hunting for the right day cell.
 *
 * UX intent (user feedback 2026-05-15):
 *   > "Today should be done in the hero, other days could be on the
 *      this week one"
 *
 * The This Week list keeps the per-day click-to-toggle for retroactive
 * logging (yesterday wasn't logged · skipped Tuesday · etc.). But the
 * PRIMARY interaction — "I just finished today's run" — happens here.
 *
 * States:
 *   - default        → solid marine `--signal` button: "Mark today complete"
 *   - pending        → disabled + dimmed, label "Marking..."
 *   - done           → warm `--today-soft` background, ✓ icon, label
 *                       "Done · Tap to undo"
 *   - rest day       → not rendered (no completion needed)
 *   - no plan / no id → not rendered (demo path, or pre-plan)
 *
 * Self-gates on:
 *   - `day` truthy AND `day.id` set (real-data path only)
 *   - day is NOT a rest day (rest days don't have a "completion" beat)
 *
 * Props are deliberately minimal — the parent computes which Day is
 * today from `weekPlan.days.find(d => d.today)`.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Day } from '../../fixtures/plan';
import { ClearLogPrompt, clearLogLine } from '../WeekPlan/ClearLogPrompt';
import styles from './MarkTodayCompleteButton.module.css';

export interface MarkTodayCompleteButtonProps {
  /** Today's Day from `weekPlan.days`. When undefined / null / no id
   *  / rest day → component returns null. */
  day: Day | null | undefined;
  /** Toggle callback wired in today.tsx. Same shape as the WeekPlan
   *  onToggle prop — the parent's `handleToggleDay` callback handles
   *  PUT + refetches. */
  onToggle?: (
    dayId: string,
    currentStatus: Day['completionStatus'],
  ) => Promise<unknown>;
  /**
   * W1-TODAY-HERO — `'v2'` is the 2.0 control (TH-7 to TH-9): "Mark done",
   * "Working…", "Done · Tap to undo" with the accessible name "Today is marked
   * done. Tap to undo.", and the same undo question (Keep it / Clear it) in the
   * 2.0 look. One write path in every look: `onToggle` is called once per press,
   * exactly as in the legacy control. Absent = the legacy control, byte for byte.
   */
  variant?: 'v2';
  /**
   * v2, not done: `'text'` is the quiet "Ran it already? Mark done" that sits
   * under "Start run" (TH-8); the default `'primary'` is the "Mark done" button
   * (TH-7). Same completion either way.
   */
  markStyle?: 'primary' | 'text';
  /**
   * v2, done: rendered beside the Done button (the effort action). It is hidden
   * together with the button while the undo question is open.
   */
  trailing?: ReactNode;
}

export function MarkTodayCompleteButton({
  day,
  onToggle,
  variant,
  markStyle = 'primary',
  trailing,
}: MarkTodayCompleteButtonProps) {
  const [pending, setPending] = useState<boolean>(false);
  // Ruling 1b: undoing a day that has a logged effort or distance asks first.
  const [confirming, setConfirming] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef<boolean>(false);

  const isCompleted = day?.completionStatus === 'completed';
  const clearLine = day && isCompleted ? clearLogLine(day) : null;
  const showConfirm = confirming && !!clearLine && !pending;

  // Back from the prompt (either answer): the button gets focus.
  useEffect(() => {
    if (!showConfirm && restoreFocus.current) {
      restoreFocus.current = false;
      buttonRef.current?.focus();
    }
  }, [showConfirm]);
  // The day stopped being a completed one with a log: nothing to ask any more.
  useEffect(() => {
    if (!clearLine) setConfirming(false);
  }, [clearLine]);

  if (!day || !day.id || day.rest || !onToggle) return null;

  const handleClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onToggle(day.id!, day.completionStatus);
    } finally {
      setPending(false);
    }
  };

  // The press: ask first when it would clear a log, else straight through.
  const press = () => {
    if (pending) return;
    if (clearLine) {
      setConfirming(true);
      return;
    }
    void handleClick();
  };
  // "Keep it" writes nothing; "Clear it" is the existing undo.
  const answer = (clear: boolean) => {
    restoreFocus.current = true;
    setConfirming(false);
    if (clear) void handleClick();
  };

  if (variant === 'v2') {
    const v2Class = [
      styles.v2Btn,
      isCompleted ? styles.v2Done : markStyle === 'text' ? styles.v2Text : styles.v2Primary,
      pending ? styles.v2Pending : '',
    ]
      .filter(Boolean)
      .join(' ');
    const v2Label = pending
      ? 'Working…'
      : isCompleted
        ? 'Done · Tap to undo'
        : markStyle === 'text'
          ? 'Ran it already? Mark done'
          : 'Mark done';
    return (
      <>
        {showConfirm && clearLine && (
          <ClearLogPrompt
            variant="v2"
            line={clearLine}
            onClear={() => answer(true)}
            onKeep={() => answer(false)}
          />
        )}
        {/* The prompt stands in for the row while it is open (hidden, not
            unmounted, so the Done control is kept and focus can come back to it). */}
        <div className={styles.v2Row} hidden={showConfirm}>
          <button
            ref={buttonRef}
            type="button"
            className={v2Class}
            onClick={press}
            // aria-disabled, not disabled: see the legacy control below.
            aria-disabled={pending || undefined}
            aria-pressed={isCompleted}
            aria-label={isCompleted ? 'Today is marked done. Tap to undo.' : undefined}
          >
            {isCompleted && (
              <span className={styles.v2Check} aria-hidden="true">
                ✓
              </span>
            )}
            <span className={styles.v2Label}>{v2Label}</span>
          </button>
          {isCompleted && trailing}
        </div>
      </>
    );
  }

  const className = [
    styles.btn,
    isCompleted ? styles.done : '',
    pending ? styles.pending : '',
  ]
    .filter(Boolean)
    .join(' ');

  const label = pending
    ? 'Working…'
    : isCompleted
      ? 'Done · Tap to undo'
      : 'Mark today complete';

  return (
    <>
      {showConfirm && clearLine && (
        <ClearLogPrompt
          className={styles.confirm}
          line={clearLine}
          onClear={() => answer(true)}
          onKeep={() => answer(false)}
        />
      )}
      <button
        ref={buttonRef}
        type="button"
        className={className}
        onClick={press}
        // The prompt stands in for the button while it is open (hidden, not
        // unmounted, so the node is kept and focus can come back to it).
        hidden={showConfirm}
        // aria-disabled, not disabled: while the undo is in flight the button
        // must keep focus (a disabled button drops it in some browsers). The
        // `pending` guard in `press` and `handleClick` blocks a second press.
        aria-disabled={pending || undefined}
        aria-pressed={isCompleted}
        aria-label={
          isCompleted
            ? 'Today is marked complete. Tap to undo.'
            : 'Mark today complete'
        }
      >
        {isCompleted && (
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
        )}
        <span className={styles.label}>{label}</span>
      </button>
    </>
  );
}
